import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { Icon } from '../atoms/Icon';
import { Menu, type MenuItem } from './Menu';
import styles from './RowActions.module.css';

/** How long a finger rests on a row before its menu opens (like iOS). */
export const LONG_PRESS_MS = 500;
/** A finger that moves further scrolls instead. */
const LONG_PRESS_SLOP = 10;

/**
 * Orders the actions of a row (docs/ux-richtlinien.md §6.1): safe ones first,
 * destructive ones last and set apart by a separator. Hidden or empty lists
 * give no menu.
 */
export function orderRowActions(items: (MenuItem | false | null | undefined)[]): MenuItem[] {
  const given = items.filter((i): i is MenuItem => Boolean(i));
  const safe = given.filter((i) => i.tone !== 'danger');
  const danger = given.filter((i) => i.tone === 'danger');
  return [
    ...safe,
    ...danger.map((item, index) => (index === 0 ? { ...item, separated: true } : item)),
  ].map((item, index) => (index === 0 && item.separated ? { ...item, separated: false } : item));
}

export interface RowActionsState {
  open: boolean;
  point: { x: number; y: number } | null;
  setOpen: (open: boolean) => void;
  /** Spread onto the row: right-click, long press and the context-menu key. */
  rowProps: {
    onContextMenu: (e: MouseEvent<HTMLElement>) => void;
    onPointerDown: (e: PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: PointerEvent<HTMLElement>) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onClickCapture: (e: MouseEvent<HTMLElement>) => void;
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  };
}

/**
 * Opens a row's menu from anywhere in the row, like in macOS and iOS: a
 * right-click opens it at the pointer, a long press at the finger, the
 * context-menu key (or Shift+F10) at the `…` button. The click that ends a
 * long press does not open the row.
 */
export function useRowActions(enabled = true): RowActionsState {
  const [open, setOpenState] = useState(false);
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
  const press = useRef<{ x: number; y: number; timer: number } | null>(null);
  const swallowClick = useRef(false);

  const cancelPress = useCallback(() => {
    if (press.current) window.clearTimeout(press.current.timer);
    press.current = null;
  }, []);
  useEffect(() => cancelPress, [cancelPress]);

  const openAt = (next: { x: number; y: number } | null) => {
    setPoint(next);
    setOpenState(true);
  };
  const setOpen = (next: boolean) => {
    if (!next) setPoint(null);
    setOpenState(next);
  };

  // Menus and dialogs of the row live inside it in the DOM, but are not the row.
  const fromMenu = (target: EventTarget) =>
    (target as Element).closest?.('[role="menu"], dialog') != null;

  return {
    open,
    point,
    setOpen,
    rowProps: {
      onContextMenu: (e) => {
        if (!enabled || e.defaultPrevented || fromMenu(e.target)) return;
        e.preventDefault();
        cancelPress();
        // The context-menu key reports no pointer position: open at the button.
        const keyboard = e.clientX === 0 && e.clientY === 0;
        openAt(keyboard ? null : { x: e.clientX, y: e.clientY });
      },
      onPointerDown: (e) => {
        swallowClick.current = false;
        if (!enabled || e.pointerType !== 'touch' || fromMenu(e.target)) return;
        cancelPress();
        const { clientX: x, clientY: y } = e;
        press.current = {
          x,
          y,
          timer: window.setTimeout(() => {
            press.current = null;
            swallowClick.current = true;
            navigator.vibrate?.(10);
            openAt({ x, y });
          }, LONG_PRESS_MS),
        };
      },
      onPointerMove: (e) => {
        const p = press.current;
        if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > LONG_PRESS_SLOP) cancelPress();
      },
      onPointerUp: cancelPress,
      onPointerCancel: cancelPress,
      onClickCapture: (e) => {
        if (!swallowClick.current) return;
        swallowClick.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
      onKeyDown: (e) => {
        if (!enabled || fromMenu(e.target)) return;
        if (e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey)) {
          e.preventDefault();
          openAt(null);
        }
      },
    },
  };
}

export interface RowActionsProps {
  /** Accessible name of the `…` button, e.g. "Weitere Aktionen: Anni". */
  label: string;
  items: MenuItem[];
  /** From `useRowActions`, so the row can open the menu, too. */
  state?: RowActionsState;
}

/**
 * The `…` menu at the end of a row (docs/ux-richtlinien.md §6.1): a quiet
 * icon with a 44 px target. On devices with a mouse it shows while the row is
 * hovered or focused, on touch devices always. `DataTable` and `ListRow` use
 * it for their `actions`; a click on it never opens the row.
 */
export function RowActions({ label, items, state }: RowActionsProps) {
  const [ownOpen, setOwnOpen] = useState(false);
  if (items.length === 0) return null;
  const open = state ? state.open : ownOpen;
  return (
    <span className={styles.root} data-row-actions="" data-open={open || undefined}>
      <Menu
        align="end"
        items={items}
        open={open}
        onOpenChange={state ? state.setOpen : setOwnOpen}
        anchorPoint={state?.point ?? null}
        trigger={(p) => (
          <button {...p} type="button" aria-label={label} className={styles.button}>
            <Icon name="more-horizontal" size="md" />
          </button>
        )}
      />
    </span>
  );
}
